import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';

import type { Connection, ConnectionSummary } from '@/shared/api/generated/catalog';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import { CONNECTOR_LABEL, addressOf } from '@/entities/connection';

import type { Checked, HarvestReading } from '../model/listing';
import { harvestReading, lastChecked } from '../model/listing';

/* The sources, as a table.
 *
 * **Why a table where this screen used cards.** A card is right when each row is a *thing with a
 * state and two actions* and there are a handful of them — which was the argument for the grid, and
 * it was answered by watching somebody use it. The question actually asked of this screen is "which
 * of these needs me", and that is a comparison down one column: a table puts five states in one
 * vertical scan where cards put them at five different heights, and the reader has to visit each card
 * to find the one that is red. The dataset catalogue is a table for the same reason and always was.
 *
 * The per-source actions moved to the source's own page with that change, and it is a better place
 * for them than a grid was: Verify, Harvest, Disable and Delete are decisions about one source, and a
 * grid of them puts a destructive control in a scroll region next to four others.
 *
 * **Five columns and no more.** Region and credential path were on the card and are not here: both
 * are facts a reader wants when they are looking at *one* source, and neither helps them choose which
 * one to open. They are on the detail page.
 */

/** How many characters of a host:port/database a cell shows before it truncates.
 *
 * The endpoint is mono and truncates rather than wraps, so a long host cannot make one row twice the
 * height of its neighbours — the density that makes this table scannable is the whole point of it
 * being a table. The full value is on the title attribute, because an operator diagnosing a
 * connection needs all of it.
 */
export interface ConnectionsTableProps {
  readonly connections: readonly Connection[];
  /** Harvest progress per source, keyed by id. Absent for a summary read that failed or is pending —
   * `harvestReading` turns that into silence rather than into "no harvest", which would be a claim
   * about the estate used to report our own failed request. */
  readonly summaries: ReadonlyMap<string, ConnectionSummary>;
  /** The clock, passed in rather than read here: a component that calls `new Date()` re-renders with
   * a different answer on every unrelated state change, and cannot be tested. */
  readonly now: Date;
}

/* The badge tone is the *state's*, never the harvest's, and that is deliberate.
 *
 * A running harvest is temporary and the state underneath it is not, so the tone answers "is this
 * source working" while the words answer "what is it doing". Letting a harvest paint the badge would
 * turn a failed source into an `info` row for the minutes a re-harvest was running.
 */
const STATE_TONE = {
  verified: 'ok',
  failed: 'danger',
  registered: 'info',
  /* Out of service is `neutral`, not `warn`. It is somebody's decision rather than a fault, and amber
   * on a deliberate state trains a reader to ignore amber. */
  disabled: 'neutral',
} as const satisfies Record<Connection['state'], BadgeTone>;

export function ConnectionsTable({
  connections,
  summaries,
  now,
}: ConnectionsTableProps): ReactElement {
  const { t } = useTranslation();

  return (
    <DataTable
      caption={t('connections.tableCaption')}
      columns={[
        { key: 'source', label: t('connections.column.name') },
        { key: 'endpoint', label: t('connections.column.endpoint') },
        { key: 'status', label: t('connections.column.state') },
        { key: 'checked', label: t('connections.column.lastChecked') },
        { key: 'open', label: t('connections.column.actions'), actions: true },
      ]}
    >
      {connections.map((connection) => (
        <tr key={connection.id}>
          <td>
            <PrimaryCell secondary={CONNECTOR_LABEL[connection.connector_type]}>
              {/* The whole name is the link, so the row's largest target is the one that opens it. */}
              <Link to={`/connections/${connection.id}`}>{connection.name}</Link>
            </PrimaryCell>
          </td>
          <td className="lq-mono lq-cell__truncate" title={addressOf(connection)}>
            {addressOf(connection)}
          </td>
          <td>
            <StatusBadge
              tone={STATE_TONE[connection.state]}
              label={statusLabel(
                t,
                connection,
                harvestReading(summaries.get(connection.id)),
              )}
              title={t(`connections.state.${connection.state}.meaning`)}
            />
          </td>
          <td className="lumiq-numeric">
            {checkedLabel(t, lastChecked(connection, now))}
          </td>
          <td className="lq-table__actions">
            <Link to={`/connections/${connection.id}`}>{t('connections.open')}</Link>
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

/* i18next's own function type rather than a hand-written signature. A narrower local alias
 * type-checks at the definition and not at the call site — `t` carries overloads for the
 * `defaultValue` forms this file uses, and a simplified alias silently drops them under
 * `exactOptionalPropertyTypes`. */
type Translate = TFunction;

/* The status cell's words.
 *
 * A running harvest wins the sentence because it is the most useful thing a reader can be told about
 * a source at that moment — and it cannot contradict the state beside it, since every path that
 * starts a harvest refuses a source out of service.
 *
 * A **partial** harvest is reported rather than folded into the state: the catalogue it produced is
 * incomplete in ways nobody has been told about, and this platform deliberately refuses to infer
 * absence from one. A **failed** one is reported for the same reason — a source whose state is still
 * `verified` and whose last harvest failed is a real and confusing pair, and naming it is what stops
 * a reader concluding the catalogue is current.
 */
function statusLabel(
  t: Translate,
  connection: Connection,
  harvest: HarvestReading,
): string {
  const state = t(`connections.state.${connection.state}.label`);
  switch (harvest.kind) {
    case 'running':
      return t('connections.harvesting', {
        observed: harvest.observed,
        discovered: harvest.discovered,
      });
    case 'starting':
      /* Deliberately not `0 of 0`: a harvest reports its discovery count once it has enumerated the
       * source, so zero means "not counted yet" and rendering it as a total would claim an empty
       * database. */
      return t('connections.harvestStarting');
    case 'partial':
      return t('connections.harvestPartial', { state });
    case 'failed':
      return t('connections.harvestFailed', { state });
    default:
      return state;
  }
}

function checkedLabel(t: Translate, checked: Checked): string {
  switch (checked.kind) {
    case 'never':
      /* Words at body size, never a dash and never a zero. "Never" is a fact about the source that a
       * reader can act on — verify it — where `—` reads as a column that does not apply. */
      return t('connections.checked.never');
    case 'justNow':
      return t('connections.checked.justNow');
    case 'minutes':
      return t('connections.checked.minutes', { count: checked.count });
    case 'hours':
      return t('connections.checked.hours', { count: checked.count });
    default:
      return t('connections.checked.days', { count: checked.count });
  }
}
